/**
 * The preview transcode's short side, in pixels.
 *
 * A constant rather than a read: the API describes the ORIGINAL only — `metadata.video` carries
 * the source's width and height, and `previewSize` is bytes — so nothing in a media payload says
 * how big its preview is. Change this when the transcode ladder changes, or the quality menu
 * starts advertising a resolution nobody is streaming.
 *
 * The owner is `ijw-fyi/worker.video.preprocessing`: `preprocessing/logic/downscale.py` defaults
 * `shortest_edge_dim` to 384 and its `_get_filter` builds `scale=-2:384` for landscape and
 * `scale=384:-2` for portrait, both on display-oriented dimensions. Nothing ties that value to
 * this one, so a ladder change there mislabels every preview row here with no signal — check
 * both when either moves. A preview width/height on the media payload would retire this.
 */
export const PREVIEW_SHORT_SIDE_PX = 384;

/**
 * The shorter side, rounded, or null when the dimensions are missing or nonsense.
 *
 * Absent dimensions are a real state, not a defensive guess: `metadata.video` does not exist
 * until processing has probed the file.
 */
function shortSidePx(width?: number, height?: number): number | null {
  if (width === undefined || height === undefined) return null;
  if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) return null;
  return Math.round(Math.min(width, height));
}

/**
 * A resolution as viewers name it — "1080p".
 *
 * The SHORTER side, so a portrait phone clip reads 1080p like the landscape footage beside it
 * rather than 1920p. Null when the dimensions are unknown; callers render nothing rather than a
 * guess.
 */
export function resolutionLabel(width?: number, height?: number): string | null {
  const shortSide = shortSidePx(width, height);
  return shortSide === null ? null : `${shortSide}p`;
}

/**
 * What the preview is worth watching at for a source of this size: the ladder's short side, or
 * the source's own if it was smaller.
 *
 * The clamp is a labelling choice, not a claim about the transcode. The worker's preview filter
 * carries no `min()` — unlike its thumbnail path, which does — so a 320x240 upload really is
 * streamed at a 384 short side. Naming the source's 240p is still the honest number, because
 * the upscale adds no detail the viewer could see; what it must not do is read as a statement
 * that the pipeline leaves small sources alone.
 *
 * Falls back to the ladder's figure when the source dimensions are unknown. That is the honest
 * answer for all but the rare below-ladder upload, and the alternative — a blank where the other
 * row has a number — reads as the preview having no resolution at all.
 */
export function previewResolutionLabel(width?: number, height?: number): string {
  const shortSide = shortSidePx(width, height);
  const previewShortSide =
    shortSide === null ? PREVIEW_SHORT_SIDE_PX : Math.min(PREVIEW_SHORT_SIDE_PX, shortSide);
  return `${previewShortSide}p`;
}
